import styles from './QueryContainer.module.css';
import { selectIcon } from '../assets/images/icons';
import { useRef, useState, useEffect, useMemo } from 'react';
import { useQuery } from '../QueryContext';
import { getCountryLabel } from '../utils/countries';

const QueryContainer = () => {
  const {
    selectedPlatform,
    selectedCountry, setSelectedCountry,
    selectedCategory, setSelectedCategory,
    selectedSubCategory, setSelectedSubCategory,
  } = useQuery();

  const selectRef = useRef(null);
  const [countries, setCountries] = useState([]);
  const [categoryRows, setCategoryRows] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [countryError, setCountryError] = useState(null);
  const [categoryError, setCategoryError] = useState(null);

  // categoryRows로부터 distinct cate 리스트와 cate -> sub_cate[] 매핑 파생
  const { cates, subCatesByCate } = useMemo(() => {
    const seen = new Set();
    const catesArr = [];
    const map = new Map();
    for (const row of categoryRows) {
      if (!seen.has(row.cate)) {
        seen.add(row.cate);
        catesArr.push(row.cate);
        map.set(row.cate, []);
      }
      const subs = map.get(row.cate);
      if (!subs.includes(row.sub_cate)) subs.push(row.sub_cate);
    }
    return { cates: catesArr, subCatesByCate: map };
  }, [categoryRows]);

  // Effect 1: selectedPlatform 변경 → country 리스트 fetch + fallback
  useEffect(() => {
    let cancelled = false;
    const fetchCountries = async () => {
      try {
        setLoadingCountries(true);
        setCountryError(null);
        const res = await fetch(`/api/country?platform=${encodeURIComponent(selectedPlatform)}`);
        if (!res.ok) throw new Error('Failed to fetch countries');
        const data = await res.json();
        if (cancelled) return;
        const list = data.success ? data.data : [];
        setCountries(list);
        const values = list.map(c => c.country);
        if (!values.includes(selectedCountry)) {
          setSelectedCountry(values[0] || '');
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching countries:', err);
        setCountryError(err.message);
      } finally {
        if (!cancelled) setLoadingCountries(false);
      }
    };
    fetchCountries();
    return () => { cancelled = true; };
  }, [selectedPlatform]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect 2: selectedPlatform/Country 변경 → category rows fetch (fallback은 Effect 3에서)
  useEffect(() => {
    if (!selectedCountry) {
      setCategoryRows([]);
      return;
    }
    let cancelled = false;
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        setCategoryError(null);
        const params = new URLSearchParams();
        params.set('platform', selectedPlatform);
        params.set('country', selectedCountry);
        const res = await fetch(`/api/category?${params}`);
        if (!res.ok) throw new Error('Failed to fetch categories');
        const data = await res.json();
        if (cancelled) return;
        setCategoryRows(data.success ? data.data : []);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching categories:', err);
        setCategoryError(err.message);
      } finally {
        if (!cancelled) setLoadingCategories(false);
      }
    };
    fetchCategories();
    return () => { cancelled = true; };
  }, [selectedPlatform, selectedCountry]);

  // Effect 3: cate/sub_cate fallback — 최신 selection 값으로 항상 검증.
  // selectedSubCategory가 deps에 포함된 이유: 사용자가 cate 클릭 후 sub_cate가
  // 유효하지 않은 상태도 검증하기 위함. setSelectedSubCategory가 같은 값을 다시
  // 쓰면 React가 bail out하므로 최대 1회 추가 실행 후 안정화된다.
  useEffect(() => {
    if (cates.length === 0) return;
    if (!cates.includes(selectedCategory)) {
      const firstCate = cates[0];
      setSelectedCategory(firstCate);
      setSelectedSubCategory((subCatesByCate.get(firstCate) || [])[0] || '');
    } else {
      const subs = subCatesByCate.get(selectedCategory) || [];
      if (!subs.includes(selectedSubCategory)) {
        setSelectedSubCategory(subs[0] || '');
      }
    }
  }, [cates, subCatesByCate, selectedCategory, selectedSubCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChevronClick = () => {
    if (selectRef.current) {
      selectRef.current.showPicker();
    }
  };

  const handleCountryChange = (event) => {
    setSelectedCountry(event.target.value);
  };

  const handleCategoryClick = (cate) => {
    setSelectedCategory(cate);
    const subs = subCatesByCate.get(cate) || [];
    setSelectedSubCategory(subs[0] || '');
  };

  const renderCountrySection = () => {
    if (loadingCountries) {
      return <div>Loading countries...</div>;
    }
    if (countryError) {
      return <div>Error loading countries: {countryError}</div>;
    }
    return (
      <>
        <select
          className={styles.selectCountries}
          ref={selectRef}
          value={selectedCountry}
          onChange={handleCountryChange}
        >
          {countries.map((country) => (
            <option key={country.country} className={styles.option} value={country.country}>
              {getCountryLabel(country.country)}
            </option>
          ))}
        </select>
        <div className={styles.chevronBackwardParent}>
          <img
            className={styles.chevronBackwardIcon}
            alt=""
            src={selectIcon}
            onClick={handleChevronClick}
            style={{ cursor: 'pointer' }}
          />
        </div>
      </>
    );
  };

  const renderCategorySection = () => {
    if (categoryError) {
      return <div>Error loading categories: {categoryError}</div>;
    }
    if (loadingCategories && cates.length === 0) {
      return <div>Loading categories...</div>;
    }
    return cates.map((cate) => (
      <div
        key={cate}
        className={`${styles.buttonWrapper} ${
          cate === selectedCategory ? styles.selected : ''
        }`}
        onClick={() => handleCategoryClick(cate)}
        style={{ cursor: 'pointer' }}
      >
        <div className={styles.labelText}>{cate}</div>
      </div>
    ));
  };

  return (
    <div className={styles.frameGroup}>
      <div className={styles.frameWrapper}>
        <div className={styles.countryWrapper}>
          <div className={styles.label}>Country</div>
          <div className={styles.frameContainer}>
            {renderCountrySection()}
          </div>
        </div>
        <div className={styles.categoryWrapper}>
          <div className={styles.label}>Category</div>
          <div className={styles.frameContainer}>
            <div className={styles.frameParent}>
              {renderCategorySection()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QueryContainer;
